import { useSuspenseQuery } from '@tanstack/react-query'
import RexService from 'services/rexService'
import { AuthContext } from '@urbanx/agx.auth.client'
import { useCallback, useContext } from 'react'
import { UpcomingSettlement } from '@urbanx/agx.ui-components2'

type StaffGetUpcomingSettlementsForAgencyKey = [
  'StaffGetUpcomingSettlementsForAgency',
  string | undefined, // agencyId
]

export interface UpcomingSettlementsForAgency {
  upcomingSettlements: UpcomingSettlement[]
  settlementsAfterAnniversary: UpcomingSettlement[]
  upcomingSalesVolume: number
  upcomingCommission: number
}

const useStaffGetUpcomingSettlementsForAgency = (
  agencyId: string | undefined
): {
  data: UpcomingSettlementsForAgency
  isPending: boolean
  isError: boolean
} => {
  const { getAuthToken } = useContext(AuthContext)

  const staffGetUpcomingSettlementsForAgency = useCallback(
    async ([
      url,
      agencyId,
    ]: StaffGetUpcomingSettlementsForAgencyKey): Promise<UpcomingSettlementsForAgency> => {
      try {
        const token = await getAuthToken()

        const service = new RexService(token)

        const result = await service.rexService.get(url, {
          agencyId: agencyId ?? '',
        })

        return result.data
      } catch (error) {
        console.error('Error fetching upcoming settlements:', error)
        throw new Error(
          `Failed to fetch upcoming settlements for agencyId: ${agencyId}`
        )
      }
    },
    [getAuthToken]
  )

  const { data, isPending, isError } = useSuspenseQuery<
    UpcomingSettlementsForAgency,
    Error,
    UpcomingSettlementsForAgency,
    StaffGetUpcomingSettlementsForAgencyKey
  >({
    queryKey: ['StaffGetUpcomingSettlementsForAgency', agencyId],
    queryFn: (queryInfo) =>
      staffGetUpcomingSettlementsForAgency(queryInfo.queryKey),
  })

  return { data, isPending, isError }
}

export default useStaffGetUpcomingSettlementsForAgency
